import { ChevronLeft, X, Info } from "lucide-react";
import { useHubStore } from "../use-hub-store";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { MOMENT_TIPS } from "../tips";

interface MomentHeaderProps {
  title: string;
  subtitle?: string;
  showBackButton?: boolean;
  showCloseButton?: boolean;
  showInfoButton?: boolean;
  className?: string;
}

export function MomentHeader({
  title,
  subtitle,
  showBackButton = true,
  showCloseButton = false,
  showInfoButton = false,
  className,
}: MomentHeaderProps) {
  const goBack = useHubStore((state) => state.goBack);
  const close = useHubStore((state) => state.close);

  return (
    <div
      className={cn(
        "flex items-center justify-between px-6 py-4",
        className
      )}
    >
      <div className="flex flex-col">
        <div className="flex items-center gap-2">
          {showBackButton && (
            <button
              type="button"
              onClick={goBack}
              className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100"
              aria-label="Go back"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}
          <h2 className="text-2xl font-bold">{title}</h2>
        </div>
        {subtitle && (
          <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
        )}
      </div>
      <div className="flex items-center gap-1 self-start">
        {showInfoButton && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 text-gray-600 transition-colors hover:bg-gray-100"
                aria-label="Tips and tricks"
              >
                <Info className="h-4 w-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              sideOffset={8}
              className="w-80 rounded-2xl p-5"
            >
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Tips and tricks
                  </h3>
                  <p className="text-sm text-gray-500">
                    A few ideas to keep moments helpful
                  </p>
                </div>
                <div className="space-y-4">
                  {MOMENT_TIPS.map((tip, index) => {
                    const Icon = tip.icon;
                    return (
                      <div key={index} className="flex gap-3">
                        <Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900">
                            {tip.title}
                          </p>
                          <p className="text-sm text-gray-500">
                            {tip.description}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )}
        {showCloseButton && (
          <button
            type="button"
            onClick={close}
            className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
}
